import React, { useRef, useState } from "react";
import { AlarmClock, BookOpenCheck, Download, FileText, FlaskConical, RotateCcw, Upload } from "lucide-react";
import { toast } from "sonner";
import { isLacwLocalTestMode, lacwLocalTestStore } from "@/lib/lacwLocalTestStore";
import { clientStorage } from "@/lib/clientStorage";
import { deviceStorage } from "@/lib/deviceStorage";
import { installRemindersDemo, startWalkthrough } from "@/lib/remindersDemo";
import LacwWalkthroughPanel from "@/components/LacwWalkthroughPanel";
import { getPortalToolPath } from "@/lib/portalToolRoutes";

function downloadJson(payload) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `lacw-local-test-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function LacwLocalTestBar() {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  if (!isLacwLocalTestMode()) return null;

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      lacwLocalTestStore.importData(JSON.parse(await file.text()));
      toast.success("LACW fictional test data imported.");
      window.location.reload();
    } catch (error) {
      toast.error(error.message || "Could not import the test data file.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    if (!window.confirm("Reset all LACW local test records to the fictional seed data?")) return;
    lacwLocalTestStore.reset();
    window.location.reload();
  };

  // Clears this browser's Aid Planner matters, diary week and file requests, and loads made-up
  // matters that show every kind of grant reminder. Opens the Aid Planner to start.
  const loadDemo = () => {
    if (!window.confirm("Load the grant reminders demo? This clears the Aid Planner matters, the current diary week and the file requests saved in this browser, and loads made-up ones.")) return;
    installRemindersDemo({ client: clientStorage, device: deviceStorage });
    window.location.assign(getPortalToolPath("lacw", "aid-planner"));
  };

  // One made-up matter taken through from file request to finalisation, a step at a time.
  const walkThrough = () => {
    if (!window.confirm("Walk through a matter? This clears the Aid Planner matters, the current diary week and the file requests saved in this browser, and starts one made-up matter.")) return;
    startWalkthrough({ client: clientStorage, device: deviceStorage });
    window.location.assign(getPortalToolPath("lacw", "aid-planner"));
  };

  return (
    <>
    <aside className="print:hidden border-b border-amber-300 bg-amber-50 text-amber-950" data-testid="lacw-local-test-bar">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3"><FlaskConical className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" /><div><p className="text-sm font-bold">Local test mode — fictional data only</p><p className="text-xs text-amber-800">Stored in this browser on this computer. No Actionstep or portal database writes.</p></div></div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => downloadJson(lacwLocalTestStore.exportData())} className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100"><Download className="h-3.5 w-3.5" /> Export</button>
          <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"><Upload className="h-3.5 w-3.5" /> Import</button>
          <a href="/demo-documents/index.html" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100"><FileText className="h-3.5 w-3.5" /> Sample letters</a>
          <button type="button" disabled={busy} onClick={walkThrough} className="inline-flex items-center gap-1.5 rounded-md border border-sky-400 bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-900 hover:bg-sky-200 disabled:opacity-50"><BookOpenCheck className="h-3.5 w-3.5" /> Walk through a matter</button>
          <button type="button" disabled={busy} onClick={loadDemo} className="inline-flex items-center gap-1.5 rounded-md border border-orange-400 bg-orange-100 px-3 py-1.5 text-xs font-semibold text-orange-900 hover:bg-orange-200 disabled:opacity-50"><AlarmClock className="h-3.5 w-3.5" /> Load reminders demo</button>
          <button type="button" disabled={busy} onClick={reset} className="inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>
          <input ref={inputRef} type="file" accept="application/json,.json" onChange={importFile} className="hidden" />
        </div>
      </div>
    </aside>
    <LacwWalkthroughPanel />
    </>
  );
}
